import React, { CSSProperties } from 'react';
import { PostResponseSchema, useGetPostMetadataQuery } from '@api/client';
import { EventInfoOverlay } from './components/EventInfoOverlay';
import { InterestedButton } from './components/InterestedButton';
import { ViewDetailsLink } from './components/ViewDetailsLink';
import UserInfoOverlay from '../components/UserInfoOverlay';

interface Props {
  post: PostResponseSchema;
  onVideoClick?: (post: PostResponseSchema) => void;
  onImageClick?: (post: PostResponseSchema) => void;
  wrapperStyle?: React.CSSProperties;
}

export default function PostCardEventPostType({
  post,
  onVideoClick,
  onImageClick,
  wrapperStyle,
}: Props): React.ReactElement {
  const { data: metadata } = useGetPostMetadataQuery(String(post.id));
  const postMetadata = metadata?.[post.id];

  const isAttending = postMetadata?.attending ?? false;
  const attendeeCount = postMetadata?.attendee_count ?? 0;

  const now = new Date();
  const startDate = post.starts_at ? new Date(post.starts_at) : null;
  const hasStarted = startDate ? startDate <= now : false;

  if (!post.starts_at) {
    console.warn('PostCardEventPostType: Event post missing starts_at');
    return <></>;
  }

  const handleBackgroundClick = () => {
    if (post.m3u8_url && onVideoClick) {
      onVideoClick(post);
    } else if (post.photo_url && onImageClick) {
      onImageClick(post);
    }
  };

  return (
    <>
      <div
        style={{
          ...styles.background,
          cursor: post.m3u8_url || post.photo_url ? 'pointer' : 'default',
        }}
        onClick={handleBackgroundClick}>
        {post.photo_url && (
          <>
            <img
              src={post.photo_url}
              alt={post.title || 'Event'}
              style={styles.backgroundImage}
            />
            <div style={styles.darkOverlay} />
          </>
        )}
      </div>

      <EventInfoOverlay
        title={post.title || 'Untitled Event'}
        description={post.message_text}
        startsAt={post.starts_at}
        endsAt={post.ends_at}
        hasImage={!!post.photo_url}
        attendeeCount={attendeeCount}
      />

      <div style={styles.actionsRow}>
        {!hasStarted && (
          <InterestedButton
            eventId={post.id}
            isAttending={isAttending}
            attendeeCount={attendeeCount}
          />
        )}
        <ViewDetailsLink shareId={post.share_id} />
      </div>

      <UserInfoOverlay
        profilePic={post.profile_pic}
        username={post.username}
        timestamp={post.timestamp}
        variant="overlay"
      />
    </>
  );
}

const styles: Record<string, CSSProperties> = {
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    backgroundColor: '#ffffff',
  },
  backgroundImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  darkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  actionsRow: {
    position: 'absolute',
    bottom: 55,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 10px',
    zIndex: 15,
  },
};
